import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApiClient } from '../lib/apiClient';
import { errorCopy } from '../lib/errors';
import { Wordmark } from '../components/Logo';
import { WEEKDAY_LABELS } from '../lib/format';

type Step = 1 | 2 | 3 | 4;

interface SalonForm {
  phone: string;
  address: string;
  city: string;
}

interface DayHours {
  weekday: number;
  open_time: string;
  close_time: string;
  closed: boolean;
}

interface ServiceDraft {
  name: string;
  price_pesos: number; // input en pesos, se convierte a centavos al guardar
  duration_min: number;
}

interface BarberDraft {
  name: string;
  email: string;
  password: string;
  commission_percent: number;
}

const DEFAULT_HOURS: DayHours[] = [
  { weekday: 0, open_time: '09:00', close_time: '20:00', closed: false },
  { weekday: 1, open_time: '09:00', close_time: '20:00', closed: false },
  { weekday: 2, open_time: '09:00', close_time: '20:00', closed: false },
  { weekday: 3, open_time: '09:00', close_time: '20:00', closed: false },
  { weekday: 4, open_time: '09:00', close_time: '20:00', closed: false },
  { weekday: 5, open_time: '09:00', close_time: '21:00', closed: false },
  { weekday: 6, open_time: '09:00', close_time: '14:00', closed: true },
];

const DEFAULT_SERVICES: ServiceDraft[] = [
  { name: 'Corte clásico', price_pesos: 5000, duration_min: 30 },
  { name: 'Corte + barba', price_pesos: 8000, duration_min: 45 },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { salon } = useAuth();
  const apiClient = useApiClient();

  const [step, setStep] = useState<Step>(1);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Step 1: salon
  const [salonForm, setSalonForm] = useState<SalonForm>({ phone: '', address: '', city: '' });

  // Step 2: hours
  const [hours, setHours] = useState<DayHours[]>(DEFAULT_HOURS);

  // Step 3: services
  const [services, setServices] = useState<ServiceDraft[]>(DEFAULT_SERVICES);

  // Step 4: barbers (opcional)
  const [barbers, setBarbers] = useState<BarberDraft[]>([]);

  async function submitStep1() {
    setError(null);
    setSubmitting(true);
    const res = await apiClient('/api/salons/me', { method: 'PATCH', body: salonForm });
    setSubmitting(false);
    if (!res.ok) { setError(errorCopy(res.error)); return; }
    setStep(2);
  }

  async function submitStep2() {
    setError(null);
    setSubmitting(true);
    const res = await apiClient('/api/salons/me/hours', {
      method: 'PUT',
      body: { hours: hours.map((h) => ({ weekday: h.weekday, open_time: h.closed ? null : h.open_time, close_time: h.closed ? null : h.close_time, closed: h.closed })) },
    });
    setSubmitting(false);
    if (!res.ok) { setError(errorCopy(res.error)); return; }
    setStep(3);
  }

  async function submitStep3() {
    setError(null);
    if (services.length === 0) { setError('Agregá al menos un servicio.'); return; }
    setSubmitting(true);
    for (const s of services) {
      const res = await apiClient('/api/services', {
        method: 'POST',
        body: { name: s.name, price_cents: Math.round(s.price_pesos * 100), duration_min: s.duration_min },
      });
      if (!res.ok) { setError(errorCopy(res.error)); setSubmitting(false); return; }
    }
    setSubmitting(false);
    setStep(4);
  }

  async function submitStep4() {
    setError(null);
    setSubmitting(true);
    for (const b of barbers) {
      const res = await apiClient('/api/users', { method: 'POST', body: b });
      if (!res.ok) { setError(errorCopy(res.error)); setSubmitting(false); return; }
    }
    setSubmitting(false);
    navigate('/app', { replace: true });
  }

  return (
    <main className="min-h-screen bg-bone">
      <nav className="border-b border-ink/10 px-6 py-3 flex items-center justify-between">
        <Wordmark className="text-ink" />
        <span className="text-sm text-ash">Paso <span className="num">{step}</span> de 4 · {salon?.name}</span>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-10">
        <Progress step={step} />

        {step === 1 && (
          <form onSubmit={(e) => { e.preventDefault(); void submitStep1(); }} className="mt-10">
            <h1 className="text-3xl font-semibold tracking-tighter mb-2">Datos del salón</h1>
            <p className="text-ink/70 mb-8">
              El teléfono y dirección los ven tus clientes cuando reservan. Si no los querés mostrar, dejalos vacíos.
            </p>
            <div className="space-y-5">
              <Input label="WhatsApp del salón" placeholder="11 1234 5678" value={salonForm.phone} onChange={(v) => setSalonForm((s) => ({ ...s, phone: v }))} />
              <Input label="Dirección" placeholder="Av. Cabildo 2345" value={salonForm.address} onChange={(v) => setSalonForm((s) => ({ ...s, address: v }))} />
              <Input label="Ciudad" placeholder="CABA" value={salonForm.city} onChange={(v) => setSalonForm((s) => ({ ...s, city: v }))} />
            </div>
            <ErrorBox error={error} />
            <Footer onNext={submitStep1} nextLabel={submitting ? 'Bancá un toque…' : 'Siguiente'} disabled={submitting} />
          </form>
        )}

        {step === 2 && (
          <div className="mt-10">
            <h1 className="text-3xl font-semibold tracking-tighter mb-2">¿Cuándo atendés?</h1>
            <p className="text-ink/70 mb-8">
              Pintá los días que abrís. Los que están cerrados, marcalos.
            </p>
            <div className="space-y-2">
              {hours.map((h, i) => (
                <DayRow key={h.weekday} hour={h} onChange={(updated) => setHours((prev) => prev.map((x, idx) => (idx === i ? updated : x)))} />
              ))}
            </div>
            <ErrorBox error={error} />
            <Footer onBack={() => setStep(1)} onNext={submitStep2} nextLabel={submitting ? 'Bancá un toque…' : 'Siguiente'} disabled={submitting} />
          </div>
        )}

        {step === 3 && (
          <div className="mt-10">
            <h1 className="text-3xl font-semibold tracking-tighter mb-2">Tus servicios</h1>
            <p className="text-ink/70 mb-8">
              Te dejé dos sugerencias para arrancar. Editalas, sumá las que quieras, sacá las que no.
            </p>
            <div className="space-y-3">
              {services.map((s, i) => (
                <ServiceRow key={i} service={s}
                  onChange={(updated) => setServices((prev) => prev.map((x, idx) => (idx === i ? updated : x)))}
                  onRemove={() => setServices((prev) => prev.filter((_, idx) => idx !== i))}
                />
              ))}
              <button type="button" onClick={() => setServices((p) => [...p, { name: '', price_pesos: 0, duration_min: 30 }])}
                className="w-full border border-dashed border-ink/20 rounded-xl py-3 text-ink/60 hover:text-ink hover:border-ink/40 transition">
                + Sumar servicio
              </button>
            </div>
            <ErrorBox error={error} />
            <Footer onBack={() => setStep(2)} onNext={submitStep3} nextLabel={submitting ? 'Bancá un toque…' : 'Siguiente'} disabled={submitting} />
          </div>
        )}

        {step === 4 && (
          <div className="mt-10">
            <h1 className="text-3xl font-semibold tracking-tighter mb-2">¿Quién más corta?</h1>
            <p className="text-ink/70 mb-8">
              Si trabajás con otros barberos, sumalos. Si sos vos solo, pasá al final.
            </p>
            <div className="space-y-3">
              {barbers.map((b, i) => (
                <BarberRow key={i} barber={b}
                  onChange={(updated) => setBarbers((prev) => prev.map((x, idx) => (idx === i ? updated : x)))}
                  onRemove={() => setBarbers((prev) => prev.filter((_, idx) => idx !== i))}
                />
              ))}
              <button type="button"
                onClick={() => setBarbers((p) => [...p, { name: '', email: '', password: '', commission_percent: 40 }])}
                className="w-full border border-dashed border-ink/20 rounded-xl py-3 text-ink/60 hover:text-ink hover:border-ink/40 transition">
                + Sumar barbero
              </button>
            </div>
            <ErrorBox error={error} />
            <Footer onBack={() => setStep(3)} onNext={submitStep4} nextLabel={submitting ? 'Guardando…' : barbers.length ? 'Listo, llevame a la agenda' : 'Soy yo solo, llevame a la agenda'} disabled={submitting} />
          </div>
        )}
      </div>
    </main>
  );
}

function Progress({ step }: { step: Step }) {
  return (
    <div className="flex items-center gap-2">
      {[1, 2, 3, 4].map((n) => (
        <div key={n} className={`h-1.5 flex-1 rounded-full transition ${n <= step ? 'bg-ink' : 'bg-ink/15'}`} />
      ))}
    </div>
  );
}

function Input({ label, value, onChange, placeholder, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-ink/80 mb-1.5 block">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full rounded-lg border border-ink/15 bg-white px-4 py-3 focus:border-forest focus:ring-2 focus:ring-forest/20 focus:outline-none" />
    </label>
  );
}

function DayRow({ hour, onChange }: { hour: DayHours; onChange: (h: DayHours) => void }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-ink/10 bg-white">
      <span className="w-24 font-medium">{WEEKDAY_LABELS[hour.weekday]}</span>
      <label className="flex items-center gap-2 text-sm text-ink/70">
        <input type="checkbox" checked={!hour.closed} onChange={(e) => onChange({ ...hour, closed: !e.target.checked })} className="accent-forest" />
        Atiendo
      </label>
      {!hour.closed && (
        <>
          <input type="time" value={hour.open_time} onChange={(e) => onChange({ ...hour, open_time: e.target.value })}
            className="num rounded border border-ink/15 px-2 py-1.5 focus:border-forest focus:outline-none" />
          <span className="text-ash">a</span>
          <input type="time" value={hour.close_time} onChange={(e) => onChange({ ...hour, close_time: e.target.value })}
            className="num rounded border border-ink/15 px-2 py-1.5 focus:border-forest focus:outline-none" />
        </>
      )}
      {hour.closed && <span className="text-ash text-sm italic ml-auto">Cerrado</span>}
    </div>
  );
}

function ServiceRow({ service, onChange, onRemove }: { service: ServiceDraft; onChange: (s: ServiceDraft) => void; onRemove: () => void }) {
  return (
    <div className="grid grid-cols-12 gap-2 p-3 rounded-lg border border-ink/10 bg-white">
      <input className="col-span-5 rounded border border-ink/15 px-3 py-2 focus:border-forest focus:outline-none" placeholder="Corte clásico" value={service.name} onChange={(e) => onChange({ ...service, name: e.target.value })} />
      <div className="col-span-3 flex items-center gap-1">
        <span className="text-ash">$</span>
        <input type="number" min={0} step={500} className="num w-full rounded border border-ink/15 px-2 py-2 text-right focus:border-forest focus:outline-none" value={service.price_pesos || ''} onChange={(e) => onChange({ ...service, price_pesos: Number(e.target.value) || 0 })} />
      </div>
      <div className="col-span-3 flex items-center gap-1">
        <input type="number" min={5} step={5} className="num w-full rounded border border-ink/15 px-2 py-2 text-right focus:border-forest focus:outline-none" value={service.duration_min} onChange={(e) => onChange({ ...service, duration_min: Number(e.target.value) || 30 })} />
        <span className="text-ash text-sm">min</span>
      </div>
      <button type="button" onClick={onRemove} className="col-span-1 text-ash hover:text-danger transition" aria-label="Quitar">✕</button>
    </div>
  );
}

function BarberRow({ barber, onChange, onRemove }: { barber: BarberDraft; onChange: (b: BarberDraft) => void; onRemove: () => void }) {
  return (
    <div className="p-3 rounded-lg border border-ink/10 bg-white space-y-2">
      <div className="grid grid-cols-12 gap-2">
        <input className="col-span-5 rounded border border-ink/15 px-3 py-2 focus:border-forest focus:outline-none" placeholder="Nombre del barbero" value={barber.name} onChange={(e) => onChange({ ...barber, name: e.target.value })} />
        <input className="col-span-5 rounded border border-ink/15 px-3 py-2 focus:border-forest focus:outline-none" placeholder="email para que entre" value={barber.email} onChange={(e) => onChange({ ...barber, email: e.target.value })} type="email" />
        <button type="button" onClick={onRemove} className="col-span-2 text-ash hover:text-danger" aria-label="Quitar">✕</button>
      </div>
      <div className="grid grid-cols-12 gap-2">
        <input className="col-span-7 rounded border border-ink/15 px-3 py-2 focus:border-forest focus:outline-none" placeholder="Contraseña temporal" value={barber.password} onChange={(e) => onChange({ ...barber, password: e.target.value })} />
        <div className="col-span-5 flex items-center gap-2">
          <span className="text-sm text-ink/70">Comisión</span>
          <input type="number" min={0} max={100} step={5} className="num w-16 rounded border border-ink/15 px-2 py-2 text-right focus:border-forest focus:outline-none" value={barber.commission_percent} onChange={(e) => onChange({ ...barber, commission_percent: Number(e.target.value) || 0 })} />
          <span className="text-ash">%</span>
        </div>
      </div>
    </div>
  );
}

function ErrorBox({ error }: { error: string | null }) {
  if (!error) return null;
  return <div className="mt-6 text-sm text-danger bg-danger/5 border border-danger/20 rounded-lg px-4 py-3">{error}</div>;
}

function Footer({ onBack, onNext, nextLabel, disabled }: { onBack?: () => void; onNext: () => void; nextLabel: string; disabled?: boolean }) {
  return (
    <div className="mt-10 flex items-center justify-between">
      {onBack ? (
        <button type="button" onClick={onBack} className="text-ink/60 hover:text-ink transition">← Atrás</button>
      ) : <span />}
      <button type="button" onClick={onNext} disabled={disabled} className="btn-primary disabled:opacity-50">{nextLabel}</button>
    </div>
  );
}
